// /brief/$id — opportunity detail.
// ObjectHeader + AwardArc + property grid + similar opportunities + linked
// awards + documents.

import { BriefColdStart } from '@/components/BriefColdStart';
import { DocViewer, type DocumentMeta } from '@/components/DocViewer';
import { LinkifiedText } from '@/components/LinkifiedText';
import { apiFetch } from '@/lib/api';
import { agencyDisplayLabel, shouldShowExpectedStart } from '@/lib/brief-property-grid';
import { compactUsd, dateOnly, relTime, ts } from '@/lib/format';
import { deriveLifecycleStage } from '@/lib/lifecycle';
import { localizedField, needsDynamicTranslation, useLocale } from '@/lib/locale';
import { useTranslated } from '@/lib/useTranslated';
import {
  AddToPipelineButton,
  AwardArc,
  EmptyState,
  ObjectHeader,
  Pill,
  type Tone,
  TrackButton,
} from '@/primitives';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authed/brief/$id')({
  component: OppDetail,
});

interface OpportunityDetail {
  id: string;
  publication_date: string;
  jurisdiction: string;
  tender_id: string | null;
  reference_number: string | null;
  ocid: string | null;
  status: string;
  notice_type: string | null;
  procurement_category: string | null;
  procurement_method: string | null;
  title: string | null;
  title_en: string | null;
  title_fr: string | null;
  description_en: string | null;
  description_fr: string | null;
  selection_criteria_en: string | null;
  agency_raw_name: string | null;
  agency_raw_name_fr: string | null;
  agency_canonical_name?: string | null;
  agency_canonical_name_fr?: string | null;
  agency_abbreviation?: string | null;
  closing_date: string | null;
  amendment_date: string | null;
  amendment_number: string | null;
  expected_start_date: string | null;
  expected_end_date: string | null;
  unspsc_codes: unknown[];
  trade_agreements: string | null;
  regions_of_delivery: string | null;
  contact_name: string | null;
  contact_email: string | null;
  org_name: string | null;
  org_address: string | null;
  notice_url_en: string | null;
  notice_url_fr: string | null;
  fetched_at: string | null;
}

interface SimilarRow {
  id: string;
  title: string | null;
  agency_raw_name: string | null;
  closing_date: string | null;
  procurement_category: string | null;
  status: string;
}

interface AwardRow {
  id: string;
  publication_date: string;
  vendor_name: string | null;
  agency_raw_name: string | null;
  contract_value: number | null;
  total_contract_value: number | null;
  award_date: string | null;
  start_date: string | null;
  end_date: string | null;
  status: string;
  title: string | null;
}

interface DocumentRow {
  document_id: string;
  filename: string | null;
  lang: string | null;
  fetch_status: string;
  extraction_status: string;
  storage_size_bytes: number | null;
  original_url: string | null;
  amendment_no: string | null;
  mime_type?: string | null;
  extracted_pages?: number | null;
}

function statusTone(status: string): Tone {
  switch (status.toLowerCase()) {
    case 'active':
      return 'primary';
    case 'awarded':
      return 'green';
    case 'expired':
    case 'cancelled':
      return 'gray';
    default:
      return 'gray';
  }
}

// Stage derivation lives in lib/lifecycle.ts so it's testable in isolation.
// See that module for the full priority order + UNKNOWN semantics.

function OppDetail() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { locale } = useLocale();
  const { t } = useTranslation();

  const opp = useQuery({
    queryKey: ['opportunity', id],
    queryFn: () => apiFetch<OpportunityDetail>(`/v1/opportunities/${id}`),
  });
  const similar = useQuery({
    enabled: !!opp.data,
    queryKey: ['opportunity', id, 'similar'],
    queryFn: () => apiFetch<{ items: SimilarRow[] }>(`/v1/opportunities/${id}/similar`),
  });
  const awards = useQuery({
    enabled: !!opp.data,
    queryKey: ['opportunity', id, 'awards'],
    queryFn: () => apiFetch<{ items: AwardRow[] }>(`/v1/opportunities/${id}/awards`),
  });
  const documents = useQuery({
    enabled: !!opp.data,
    queryKey: ['opportunity', id, 'documents'],
    queryFn: () => apiFetch<{ items: DocumentRow[] }>(`/v1/opportunities/${id}/documents`),
  });

  // In-app document viewer state. The Drawer is opened by clicking OPEN on
  // any docs-table row; the row has all the metadata DocViewer needs, so no
  // second fetch round-trip. /v1/documents/:id is reserved for cases where
  // the doc id arrives without context (e.g. an audit-row drill-down).
  const [openDoc, setOpenDoc] = useState<DocumentMeta | null>(null);

  // Locale-aware title + description.
  //
  // Hook MUST run unconditionally before any early return — React #310
  // fires if useTranslated only runs when opp.data is loaded (the
  // early-return branches below short-circuit some renders).
  // useTranslated handles an undefined entity_id gracefully.
  const dynamic = needsDynamicTranslation(locale);
  const tx = useTranslated(
    'tender',
    dynamic ? opp.data?.id : undefined,
    ['title', 'description'],
    locale,
  );

  if (opp.isLoading) {
    return (
      <div style={{ flex: 1, position: 'relative' }}>
        <div className="progress" />
        <div className="meta mono" style={{ padding: 24 }}>
          resolving opportunity…
        </div>
      </div>
    );
  }
  if (opp.error || !opp.data) {
    return (
      <EmptyState
        glyph="OPP"
        reason="Opportunity not found"
        detail={`No record for id ${id}. It may have been deleted or you don't have access.`}
        actions={
          <button type="button" className="btn" onClick={() => navigate({ to: '/horizon' })}>
            BACK TO DISCOVER
          </button>
        }
      />
    );
  }

  const o = opp.data;
  // `tx` and `dynamic` are declared above the early returns so the hook
  // count stays stable. EN/FR pull straight from `${base}_${locale}`
  // columns via localizedField; ZH/ES/AR/DE/PT/JA prefer `tx` when its
  // /v1/translate/entity result arrives, falling back through the
  // column chain (`_fr` → `_en` → base) while the call is in flight.
  const fallbackTitle =
    localizedField(o as unknown as Record<string, unknown>, 'title', locale) ??
    o.title ??
    `(untitled · ${o.tender_id ?? id})`;
  const fallbackDescription =
    localizedField(o as unknown as Record<string, unknown>, 'description', locale) ?? null;
  const title = tx.fields.title?.text ?? fallbackTitle;
  const description = tx.fields.description?.text ?? fallbackDescription;
  const stage = deriveLifecycleStage(o, awards.data?.items ?? []);
  const days = o.closing_date
    ? Math.round((new Date(o.closing_date).getTime() - Date.now()) / 86_400_000)
    : null;
  // Prefer the canonical name from the agencies table when the resolver has
  // linked this row; fall back to the raw payload name so unresolved rows
  // still render. See services/api/src/routes/opportunities.ts (`:id`).
  const displayedAgency = o.agency_canonical_name ?? o.agency_raw_name ?? null;

  return (
    <div
      style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', minHeight: 0 }}
    >
      <ObjectHeader
        type="OPP"
        id={o.tender_id ?? id.slice(0, 8)}
        title={title}
        sub={`${o.jurisdiction} · ${o.procurement_category ?? '—'} · ${o.notice_type ?? o.procurement_method ?? '—'}`}
        pills={[
          { tone: statusTone(o.status), label: o.status.toUpperCase() },
          ...(days != null && days >= 0
            ? [
                {
                  tone: (days < 7 ? 'red' : days < 30 ? 'amber' : 'primary') as Tone,
                  label: `${days}D LEFT`,
                },
              ]
            : []),
        ]}
        metrics={[
          { k: 'PUBLISHED', v: dateOnly(o.publication_date) },
          {
            k: 'CLOSING',
            v: dateOnly(o.closing_date),
            tone: days != null && days < 7 ? 'rd' : undefined,
          },
          { k: 'AGENCY', v: displayedAgency ? truncateMetric(displayedAgency, 14) : '—' },
        ]}
        actions={
          <>
            {/* Wrappers expose data-action targets for the cold-start CTA
                focus helpers. Keep the prim components untouched. */}
            <span data-action="track" style={{ display: 'inline-flex' }}>
              <TrackButton entityType="tender" entityId={id} />
            </span>
            <span data-action="add-to-pipeline" style={{ display: 'inline-flex' }}>
              <AddToPipelineButton entityType="tender" entityId={id} />
            </span>
            <button type="button" className="btn sm gh" disabled>
              ADD TO CASE
            </button>
            <button type="button" className="btn sm pri" disabled>
              START BID
            </button>
          </>
        }
      />

      <AwardArc stage={stage} />

      <div
        style={{
          padding: '16px 16px 0',
          display: 'grid',
          gridTemplateColumns: '1.6fr 1fr',
          gap: 12,
        }}
      >
        {/* Left: property grid */}
        <div className="pane" style={{ minHeight: 0 }}>
          <div className="pane-h">
            <span>{t('brief.pane.record')}</span>
            <span className="meta mono">{o.id}</span>
          </div>
          <div className="pane-body">
            <PropertyGrid rows={buildPropertyRows(o)} />
          </div>
        </div>

        {/* Right: description + similar + linked awards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
          {description && (
            <div className="pane">
              <div className="pane-h">
                <span>{t('brief.pane.description')}</span>
              </div>
              <div className="pane-body" style={{ padding: '10px 12px' }}>
                <p
                  className="prose"
                  style={{ fontSize: 12, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}
                >
                  <LinkifiedText text={description} />
                </p>
              </div>
            </div>
          )}

          <div className="pane">
            <div className="pane-h">
              <span>{t('brief.pane.linkedAwards')}</span>
              <span className="meta mono">{awards.data?.items.length ?? 0}</span>
            </div>
            <div className="pane-body">
              {awards.isLoading ? (
                <div className="meta mono" style={{ padding: 12 }}>
                  {t('brief.empty.loading')}
                </div>
              ) : (awards.data?.items ?? []).length === 0 ? (
                <div className="empty">
                  <div className="empty-reason">{t('brief.empty.noAwards')}</div>
                  <div className="empty-detail">{t('brief.empty.noAwardsDetail')}</div>
                </div>
              ) : (
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>{t('brief.col.date')}</th>
                      <th>{t('brief.col.vendor')}</th>
                      <th className="r">{t('brief.col.value')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {awards.data?.items.map((a) => {
                      const value = a.total_contract_value ?? a.contract_value;
                      return (
                        <tr key={a.id}>
                          <td className="mn dim">{dateOnly(a.publication_date)}</td>
                          <td>{a.vendor_name ?? '—'}</td>
                          <td className="r mn">{value != null ? compactUsd(value) : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="pane">
            <div className="pane-h">
              <span>{t('brief.pane.documents')}</span>
              <span className="meta mono">{documents.data?.items.length ?? 0}</span>
            </div>
            <div className="pane-body">
              {documents.isLoading ? (
                <div className="meta mono" style={{ padding: 12 }}>
                  {t('brief.empty.loading')}
                </div>
              ) : (documents.data?.items ?? []).length === 0 ? (
                <div className="empty">
                  <div className="empty-reason">{t('brief.empty.noDocs')}</div>
                  <div className="empty-detail">
                    {o.notice_url_en || o.notice_url_fr
                      ? t('brief.empty.noDocsPending')
                      : t('brief.empty.noDocsNoUrl')}
                  </div>
                </div>
              ) : (
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>{t('brief.col.file')}</th>
                      <th style={{ width: 44 }}>{t('brief.col.lang')}</th>
                      <th style={{ width: 72 }}>{t('brief.col.fetch')}</th>
                      <th style={{ width: 80 }}>{t('brief.col.extract')}</th>
                      <th className="r" style={{ width: 72 }}>
                        {t('brief.col.size')}
                      </th>
                      <th className="r" style={{ width: 64 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {documents.data?.items.map((d) => {
                      const canOpen = d.fetch_status === 'fetched';
                      // When we couldn't (or didn't) download the bytes, fall back
                      // to the source link so the operator can always reach the
                      // document at its origin. The whole row click-throughs there.
                      const srcUrl = !canOpen ? d.original_url : null;
                      return (
                        <tr
                          key={d.document_id}
                          onClick={
                            canOpen
                              ? () => setOpenDoc(toDocMeta(d))
                              : srcUrl
                                ? () => window.open(srcUrl, '_blank', 'noopener,noreferrer')
                                : undefined
                          }
                          style={canOpen || srcUrl ? { cursor: 'pointer' } : undefined}
                          title={
                            canOpen
                              ? 'open in viewer'
                              : srcUrl
                                ? 'open source in a new tab'
                                : `not available (${d.fetch_status})`
                          }
                        >
                          <td>{d.filename ?? `${d.document_id.slice(0, 8)}…`}</td>
                          <td className="mn">{d.lang ?? '—'}</td>
                          <td className="mn">
                            <Pill
                              tone={
                                d.fetch_status === 'fetched'
                                  ? 'green'
                                  : d.fetch_status === 'failed'
                                    ? 'red'
                                    : d.fetch_status === 'skipped'
                                      ? 'primary'
                                      : 'gray'
                              }
                              dot={false}
                            >
                              {/* 'skipped' = a reference link (portal/award page), not a
                                  fetchable file — show it as 'link', which is what it is. */}
                              {d.fetch_status === 'skipped' ? 'link' : d.fetch_status}
                            </Pill>
                          </td>
                          <td className="mn">
                            {/* Extraction only applies once bytes are downloaded;
                                for link/failed rows show '—', never 'pending'. */}
                            {canOpen ? (
                              <Pill
                                tone={d.extraction_status === 'extracted' ? 'green' : 'gray'}
                                dot={false}
                              >
                                {d.extraction_status}
                              </Pill>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="r mn">
                            {d.storage_size_bytes != null
                              ? `${ts(Math.round(d.storage_size_bytes / 1024))}KB`
                              : '—'}
                          </td>
                          <td className="r">
                            {canOpen ? (
                              <button
                                type="button"
                                className="btn xs gh"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenDoc(toDocMeta(d));
                                }}
                                title="open in terminal"
                              >
                                OPEN
                              </button>
                            ) : srcUrl ? (
                              <a
                                className="btn xs gh"
                                href={srcUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                title="open source document in a new tab"
                              >
                                SOURCE ↗
                              </a>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* In-app document viewer — Drawer-mounted, opens above the page. */}
      <DocViewer doc={openDoc} onClose={() => setOpenDoc(null)} />

      <div className="pane" style={{ margin: '12px 16px 16px' }}>
        <div className="pane-h">
          <span>SIMILAR OPPORTUNITIES</span>
          <span className="meta mono">{similar.data?.items.length ?? 0}</span>
        </div>
        <div className="pane-body">
          {similar.isLoading ? (
            <div className="meta mono" style={{ padding: 12 }}>
              {t('brief.empty.loading')}
            </div>
          ) : (similar.data?.items ?? []).length === 0 ? (
            <div className="empty">
              <div className="empty-reason">{t('brief.empty.noSimilar')}</div>
              <div className="empty-detail">{t('brief.empty.noSimilarDetail')}</div>
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('brief.col.title')}</th>
                  <th>{t('brief.col.agency')}</th>
                  <th>{t('brief.col.category')}</th>
                  <th className="r">{t('brief.col.closing')}</th>
                  <th>{t('brief.col.status')}</th>
                </tr>
              </thead>
              <tbody>
                {similar.data?.items.map((s) => (
                  <tr
                    key={s.id}
                    onClick={() => navigate({ to: '/brief/$id', params: { id: s.id } })}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{s.title ?? '—'}</td>
                    <td className="dim">{truncate(s.agency_raw_name, 28)}</td>
                    <td className="mn dim">{s.procurement_category ?? '—'}</td>
                    <td className="r mn">{dateOnly(s.closing_date)}</td>
                    <td className="mn dim">{s.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* W4-C: per-contract cold-start panel (replaces the workspace
          AI-rail cold-start text). Sticky-bottom of the scrollable brief
          surface; dismissed per-opp via localStorage so each new contract
          gets one bite. Plain HTML — no LLM calls (Iron Rule #4). */}
      <BriefColdStart oppId={id} />
    </div>
  );
}

// Property-grid row tuple. Third arg = render as prose (sans-serif, multi-line
// readable) instead of mono. Value may be a string or a ReactNode (e.g. an
// anchor for notice URLs).
//
// NOTE — Iron Rule #11: when a second surface needs this same shape (Vendor
// brief, Agency brief, Award brief), graduate this primitive to
// packages/ui/PropertyGrid. Keep the prop shape stable so the migration is a
// rename. The .pg CSS in tokens.css is already shared across surfaces.
export type PropertyGridRow = [label: string, value: ReactNode, prose?: boolean];

function PropertyGrid({ rows }: { rows: PropertyGridRow[] }) {
  return (
    <div className="pg">
      {rows.map(([k, v, prose]) => (
        <div key={k} className="pg-row">
          <div className="k">{k}</div>
          <div className={`v${prose ? ' prose' : ''}`}>{v}</div>
        </div>
      ))}
    </div>
  );
}

// Render a notice URL as a hyperlink with the trailing arrow glyph.
// Iron Rule: rel="noopener noreferrer" + target="_blank" + visible focus state.
function NoticeLink({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="notice-link"
      data-testid="notice-link"
    >
      {label} ↗
    </a>
  );
}

function contactDisplay(name: string | null, email: string | null): string {
  if (name && email) return `${name} <${email}>`;
  return name ?? email ?? '—';
}

// Build the property-grid rows for an opportunity. Pulled out of the JSX so
// each field's visibility rule (esp. shouldShowExpectedStart) stays
// readable and testable. See lib/brief-property-grid.ts for the helpers and
// tests/brief-property-grid.test.ts for coverage.
export function buildPropertyRows(o: OpportunityDetail, t?: TFunction): PropertyGridRow[] {
  // L(key, fallback): translate when a TFunction is supplied (the component),
  // else return the English fallback. The fallback keeps buildPropertyRows
  // testable without an i18n context — tests call buildPropertyRows(o) and
  // assert against the English labels.
  const L = (key: string, fallback: string): string => (t ? t(key) : fallback);
  const agencyLabel = agencyDisplayLabel(o);
  // Show FR agency only when different from the EN label (avoids duplicating
  // the same string for English-only agencies). Pre-W5 we render alongside;
  // post-W5 i18n it collapses behind the locale toggle.
  const frAgency = o.agency_canonical_name_fr ?? o.agency_raw_name_fr ?? null;
  const showFrAgency =
    !!frAgency && frAgency !== agencyLabel && frAgency !== o.agency_canonical_name;

  const closingValue: ReactNode = o.closing_date ? (
    <span data-testid="closing-cell">
      {dateOnly(o.closing_date)}
      <span className="meta" style={{ marginLeft: 8, opacity: 0.7 }}>
        {relTime(o.closing_date)}
      </span>
    </span>
  ) : (
    '—'
  );

  const noticeEnValue: ReactNode = o.notice_url_en ? (
    <NoticeLink href={o.notice_url_en} label="Notice (EN)" />
  ) : (
    '—'
  );

  const rows: PropertyGridRow[] = [
    [L('brief.field.tenderId', 'TENDER ID'), o.tender_id ?? '—'],
    [L('brief.field.reference', 'REFERENCE'), o.reference_number ?? '—'],
    [L('brief.field.ocid', 'OCID'), o.ocid ?? '—'],
    [L('brief.field.jurisdiction', 'JURISDICTION'), o.jurisdiction],
    [L('brief.field.noticeType', 'NOTICE TYPE'), o.notice_type ?? '—'],
    [L('brief.field.category', 'CATEGORY'), o.procurement_category ?? '—'],
    [L('brief.field.method', 'METHOD'), o.procurement_method ?? '—'],
    [L('brief.field.agency', 'AGENCY'), agencyLabel ?? '—', true],
  ];
  if (showFrAgency && frAgency) {
    rows.push([L('brief.field.agencyFr', 'AGENCY (FR)'), frAgency, true]);
  }
  rows.push(
    [L('brief.field.orgName', 'ORG NAME'), o.org_name ?? '—', true],
    [L('brief.field.orgAddress', 'ORG ADDRESS'), o.org_address ?? '—', true],
    [L('brief.field.published', 'PUBLISHED'), dateOnly(o.publication_date)],
    [L('brief.field.closing', 'CLOSING'), closingValue],
  );
  if (o.amendment_date) {
    rows.push([L('brief.field.amendmentDate', 'AMENDMENT DATE'), dateOnly(o.amendment_date)]);
  }
  // Hide EXPECTED START when it duplicates the row's creation anchor — the
  // scraper sometimes populates it with publication_date / fetched_at and the
  // field reads as misleading (Youssef feedback 2026-05-11).
  if (shouldShowExpectedStart(o.expected_start_date, o.publication_date, o.fetched_at)) {
    rows.push([L('brief.field.expectedStart', 'EXPECTED START'), dateOnly(o.expected_start_date)]);
  }
  if (o.expected_end_date) {
    rows.push([L('brief.field.expectedEnd', 'EXPECTED END'), dateOnly(o.expected_end_date)]);
  }
  rows.push(
    [L('brief.field.tradeAgreements', 'TRADE AGREEMENTS'), o.trade_agreements ?? '—', true],
    [L('brief.field.regions', 'REGIONS'), o.regions_of_delivery ?? '—', true],
    [L('brief.field.contact', 'CONTACT'), contactDisplay(o.contact_name, o.contact_email), true],
    [L('brief.field.noticeEn', 'NOTICE (EN)'), noticeEnValue, true],
  );
  if (o.notice_url_fr) {
    rows.push([
      L('brief.field.noticeFr', 'NOTICE (FR)'),
      <NoticeLink key="fr" href={o.notice_url_fr} label="Notice (FR)" />,
      true,
    ]);
  }
  rows.push(
    [
      L('brief.field.unspsc', 'UNSPSC CODES'),
      Array.isArray(o.unspsc_codes) && o.unspsc_codes.length > 0
        ? t
          ? t('brief.unspscCount', { count: o.unspsc_codes.length })
          : `${o.unspsc_codes.length} codes`
        : '—',
    ],
    [L('brief.field.fetched', 'FETCHED'), o.fetched_at ? relTime(o.fetched_at) : '—'],
  );
  return rows;
}

// Re-export OpportunityDetail so tests in tests/ can import the canonical
// shape without forking it.
export type { OpportunityDetail };

// Map a DocumentRow (joined attachment payload from the opportunities/:id/documents
// endpoint) onto the canonical DocumentMeta shape consumed by <DocViewer/>.
// content_url and text_url are computed here rather than expected from the
// server because a single API change to the URL convention shouldn't ripple
// through every consumer — the wire payload stays minimal, the client builds
// the URLs.
function toDocMeta(d: DocumentRow): DocumentMeta {
  return {
    id: d.document_id,
    filename: d.filename,
    mime_type: d.mime_type ?? 'application/pdf',
    storage_size_bytes: d.storage_size_bytes,
    extracted_pages: d.extracted_pages ?? null,
    fetch_status: d.fetch_status,
    extraction_status: d.extraction_status,
    lang: d.lang,
    amendment_no: d.amendment_no,
    content_url: `/v1/documents/${d.document_id}/content`,
    text_url: d.extraction_status === 'extracted' ? `/v1/documents/${d.document_id}/text` : null,
    original_url: d.original_url,
  };
}

function truncate(s: string | null | undefined, n = 30): string {
  if (!s) return '—';
  return s.length <= n ? s : `${s.slice(0, n - 1)}…`;
}
function truncateMetric(s: string, n: number): string {
  return s.length <= n ? s : `${s.slice(0, n - 1)}…`;
}
